import { compileEnergyExpression } from "./energy-expression";
import { expressionTerm, validateElementProgram, type VertexElementProgram } from "./element-program";

export function generateTinyAdVertexHeader(source: string): string {
  const expression = compileEnergyExpression(source).cppExpression;
  return `#pragma once

#include <TinyAD/ScalarFunction.hh>

#include <array>

// Generated by Direction Field Lab's live-energy workshop.
// The element stencil and global sparse assembly remain in the surrounding project.
struct LiveVertexTarget {
  std::array<double, 2> vector{0.0, 0.0};
};

template <typename Element>
auto live_vertex_energy(
    Element& element,
    const int vertex,
    const LiveVertexTarget& target,
    const double data_weight,
    const double unit_weight,
    const double target_length) -> TINYAD_SCALAR_TYPE(element) {
  using Scalar = TINYAD_SCALAR_TYPE(element);
  const auto field = element.variables(vertex);
  const Scalar ux = field[0];
  const Scalar uy = field[1];
  const double tx = target.vector[0];
  const double ty = target.vector[1];
  const double data = data_weight;
  const double unit = unit_weight;
  const double length = target_length;
  return ${expression};
}
`;
}

export function generatePythonVertexModule(source: string): string {
  const expression = compileEnergyExpression(source).pythonExpression;
  return `"""Generated by Direction Field Lab's live-energy workshop."""


def live_vertex_energy(u, target, data, unit, length):
    """Evaluate one vertex term; use an AD array type to differentiate it."""
    ux, uy = u
    tx, ty = target
    return ${expression}
`;
}

export function generateTinyAdElementProgramHeader(value: VertexElementProgram): string {
  const program = validateElementProgram(value);
  const expression = compileEnergyExpression(expressionTerm(program).expression).cppExpression;
  return `#pragma once

#include <TinyAD/ScalarFunction.hh>

#include <array>

// Generated from ${program.id}.element-program.json.
// Vertex, edge, and face callbacks below mirror the browser's sparse element ranges.
struct GeneratedProgramParameters {
  double data_weight = ${program.parameters.dataWeight};
  double unit_weight = ${program.parameters.unitWeight};
  double target_length = ${program.parameters.targetLength};
  double smoothness_weight = ${program.parameters.smoothnessWeight};
  double integrability_weight = ${program.parameters.integrabilityWeight};
};

struct GeneratedVertexTarget {
  std::array<double, 2> vector{0.0, 0.0};
};

struct GeneratedConnection {
  int tail = -1;
  int head = -1;
  std::array<double, 4> rotation{1.0, 0.0, 0.0, 1.0};
};

struct GeneratedTriangle {
  std::array<int, 3> vertices{-1, -1, -1};
  // Derivative of boundary circulation in each vertex's local tangent frame.
  std::array<std::array<double, 2>, 3> circulation_coefficients{};
  double inverse_area = 1.0;
};

template <typename Element>
auto generated_vertex_energy(
    Element& element,
    const int vertex,
    const GeneratedVertexTarget& target,
    const GeneratedProgramParameters& parameters) -> TINYAD_SCALAR_TYPE(element) {
  using Scalar = TINYAD_SCALAR_TYPE(element);
  const auto field = element.variables(vertex);
  const Scalar ux = field[0];
  const Scalar uy = field[1];
  const double tx = target.vector[0];
  const double ty = target.vector[1];
  const double data = parameters.data_weight;
  const double unit = parameters.unit_weight;
  const double length = parameters.target_length;
  return ${expression};
}

template <typename Element>
auto generated_connection_energy(
    Element& element,
    const GeneratedConnection& connection,
    const GeneratedProgramParameters& parameters) -> TINYAD_SCALAR_TYPE(element) {
  using Scalar = TINYAD_SCALAR_TYPE(element);
  const auto tail = element.variables(connection.tail);
  const auto head = element.variables(connection.head);
  const Scalar transported_x = connection.rotation[0] * tail[0] + connection.rotation[1] * tail[1];
  const Scalar transported_y = connection.rotation[2] * tail[0] + connection.rotation[3] * tail[1];
  const Scalar error_x = head[0] - transported_x;
  const Scalar error_y = head[1] - transported_y;
  return 0.5 * parameters.smoothness_weight * (error_x * error_x + error_y * error_y);
}

template <typename Element>
auto generated_circulation_energy(
    Element& element,
    const GeneratedTriangle& triangle,
    const GeneratedProgramParameters& parameters) -> TINYAD_SCALAR_TYPE(element) {
  using Scalar = TINYAD_SCALAR_TYPE(element);
  Scalar circulation = 0.0;
  for (int corner = 0; corner < 3; ++corner) {
    const auto field = element.variables(triangle.vertices[corner]);
    circulation += triangle.circulation_coefficients[corner][0] * field[0] +
                   triangle.circulation_coefficients[corner][1] * field[1];
  }
  return 0.5 * parameters.integrability_weight * triangle.inverse_area *
         circulation * circulation;
}
`;
}

export function generatePythonElementProgram(value: VertexElementProgram): string {
  const program = validateElementProgram(value);
  const expression = compileEnergyExpression(expressionTerm(program).expression).pythonExpression;
  return `"""Generated from ${program.id}.element-program.json."""

PARAMETERS = {
    "dataWeight": ${program.parameters.dataWeight},
    "unitWeight": ${program.parameters.unitWeight},
    "targetLength": ${program.parameters.targetLength},
    "smoothnessWeight": ${program.parameters.smoothnessWeight},
    "integrabilityWeight": ${program.parameters.integrabilityWeight},
}


def vertex_energy(u, target, parameters=PARAMETERS):
    ux, uy = u
    tx, ty = target
    data = parameters["dataWeight"]
    unit = parameters["unitWeight"]
    length = parameters["targetLength"]
    return ${expression}


def connection_energy(tail, head, rotation, parameters=PARAMETERS):
    transported = rotation @ tail
    error = head - transported
    return 0.5 * parameters["smoothnessWeight"] * (error @ error)


def circulation_energy(vertex_values, coefficients, inverse_area, parameters=PARAMETERS):
    circulation = sum(coefficients[i] @ vertex_values[i] for i in range(3))
    return 0.5 * parameters["integrabilityWeight"] * inverse_area * circulation**2
`;
}
